/**
 * PagosTab — list/create/edit payments scoped to one investor (+ optional villa).
 *
 * Reuses existing endpoints:
 *   GET   /payments
 *   POST  /payments
 *   PATCH /payments/{id}
 *   POST  /payments/{id}/archive
 *
 * V1: no receipt URL field — receipts can be uploaded as financial documents
 * via the Documentos tab.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Plus, CheckCircle, Archive } from "@phosphor-icons/react";

const STATUS_LABEL = {
  pending: "Pendiente",
  paid: "Pagado",
  overdue: "Vencido",
  archived: "Archivado",
};

const STATUS_TONE = {
  pending:  "bg-amber-50 text-amber-800 border-amber-200",
  paid:     "bg-emerald-50 text-emerald-700 border-emerald-200",
  overdue:  "bg-red-50 text-red-700 border-red-200",
  archived: "bg-stone-100 text-stone-500 border-stone-200",
};

// Optional classification for a payment. The first option ("") tells the
// backend "no phase" — never persisted as the string "other". Admin always
// gives the payment a free-text name via the `title` field.
const PHASE_OPTIONS = [
  { value: "",             label: "Sin tipo" },
  { value: "reservation",  label: "Reserva" },
  { value: "foundation",   label: "Cimentación" },
  { value: "structure",    label: "Estructura" },
  { value: "handover",     label: "Entrega" },
];

// Phase → canonical label fallback used when a legacy payment has no `title`.
// Mirrors the small set we surface today; anything unknown shows the raw value.
// Exported so other admin payment surfaces (AdminDashboard PaymentsAdmin) can
// resolve the same visible label without duplicating the mapping.
export const LEGACY_PHASE_LABEL = {
  reservation: "Reserva",
  foundation: "Cimentación",
  structure: "Estructura",
  handover: "Entrega",
};

function eur(n) {
  const v = Number(n) || 0;
  return v.toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
}

// Resolve the visible name of a payment row.
//   1. title trimmed (the new source of truth).
//   2. legacy auto-payment canonical phase like "Pago 1 · Reserva (10%)" → use raw.
//   3. legacy short phase ("reservation" / "foundation" / …) → translated.
//   4. final fallback: "Pago".
export function paymentLabel(p) {
  const title = (p?.title || "").trim();
  if (title) return title;
  const phase = (p?.phase || "").trim();
  if (!phase) return "Pago";
  if (phase === "other") return "Pago";
  if (LEGACY_PHASE_LABEL[phase]) return LEGACY_PHASE_LABEL[phase];
  return phase;
}

// -------- Duplicate detection (frontend-only, V1) --------
// Signature: 5 fields that, if identical across two active payments, make
// the pair almost certainly a duplicate. Archived rows are excluded upstream
// so archiving one of the pair clears the badge from the survivor.
export function paymentDupKey(p) {
  return [
    p?.investor_user_id || "",
    p?.villa_id || "",
    ((p?.phase || "") + "").trim().toLowerCase(),
    p?.due_date || "",
    Number(p?.amount) || 0,
  ].join("|");
}

// Returns:
//   - dupGroups: Map<key, payments[]> for every key with >1 active row
//   - dupSet:    Set<payment_id> for O(1) row-level lookup
//   - dupCount:  total number of active rows involved in duplicate groups
// The hook is a plain pure function under useMemo — safe to reuse anywhere.
export function useDuplicatePayments(payments) {
  return useMemo(() => {
    const groups = new Map();
    for (const p of payments || []) {
      if (p?.status === "archived") continue;
      const k = paymentDupKey(p);
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(p);
    }
    const dupGroups = new Map();
    const dupSet = new Set();
    let dupCount = 0;
    for (const [k, arr] of groups.entries()) {
      if (arr.length > 1) {
        dupGroups.set(k, arr);
        dupCount += arr.length;
        for (const p of arr) {
          if (p?.payment_id) dupSet.add(p.payment_id);
        }
      }
    }
    return { dupGroups, dupSet, dupCount };
  }, [payments]);
}

export default function PagosTab({ investor, villa, villas = [] }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showOnlyDuplicates, setShowOnlyDuplicates] = useState(false);
  const [form, setForm] = useState({
    villa_id: "",
    title: "",
    phase: "",
    amount: "",
    currency: "EUR",
    due_date: "",
    status: "pending",
  });
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    if (!investor) return;
    setLoading(true);
    try {
      const { data } = await api.get("/payments");
      const mine = (data || []).filter((p) => p.investor_user_id === investor.user_id);
      setPayments(mine);
      setError("");
    } catch (e) {
      setError(formatError(e));
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investor?.user_id]);

  const resetForm = () => {
    setForm({
      villa_id: villa?.villa_id || "",
      title: "",
      phase: "",
      amount: "",
      currency: "EUR",
      due_date: "",
      status: "pending",
    });
    setEditId(null);
  };

  const startEdit = (p) => {
    setEditId(p.payment_id);
    // Pre-fill `title` with the resolved label so legacy payments without an
    // explicit title also get a sensible starting value the admin can keep
    // or overwrite.
    setForm({
      villa_id: p.villa_id || "",
      title: (p.title && String(p.title).trim()) || paymentLabel(p),
      // Normalize the legacy "other" sentinel to "" so the new select treats
      // it as "Sin tipo" instead of showing a missing option.
      phase: p.phase && p.phase !== "other" && LEGACY_PHASE_LABEL[p.phase] ? p.phase : "",
      amount: String(p.amount ?? ""),
      currency: p.currency || "EUR",
      due_date: p.due_date || "",
      status: p.status || "pending",
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!investor) return;
    const title = String(form.title || "").trim();
    if (!title) {
      setError("Escribe un nombre para el pago.");
      return;
    }
    if (!form.amount || Number(form.amount) <= 0) {
      setError("El importe debe ser mayor que 0.");
      return;
    }
    const targetVilla = form.villa_id || villa?.villa_id;
    if (!targetVilla) {
      setError("Selecciona una villa para este pago.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const payload = {
        investor_user_id: investor.user_id,
        villa_id: targetVilla,
        title,
        // Empty string from the select means "Sin tipo" — send null so the
        // backend stores no phase rather than the literal "".
        phase: form.phase ? form.phase : null,
        amount: Number(form.amount),
        currency: form.currency || "EUR",
        due_date: form.due_date || null,
        status: form.status,
      };
      if (editId) {
        await api.patch(`/payments/${editId}`, payload);
      } else {
        await api.post("/payments", payload);
      }
      resetForm();
      setShowForm(false);
      await load();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const markPaid = async (p) => {
    try {
      await api.patch(`/payments/${p.payment_id}`, { status: "paid" });
      await load();
    } catch (e) {
      setError(formatError(e));
    }
  };

  const archive = async (p) => {
    if (!window.confirm("¿Archivar este pago? Se ocultará al inversor pero se conservará el histórico.")) return;
    try {
      await api.post(`/payments/${p.payment_id}/archive`, {});
      await load();
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "paid_payment_requires_force") {
        if (window.confirm("Este pago está marcado como pagado. ¿Archivar de todas formas? Se conservará el histórico contable.")) {
          try {
            await api.post(`/payments/${p.payment_id}/archive?force=true`, {});
            await load();
            return;
          } catch (err) {
            setError(formatError(err));
            return;
          }
        }
        return;
      }
      setError(formatError(e));
    }
  };

  const totals = useMemo(() => {
    const active = payments.filter((p) => p.status !== "archived");
    return {
      paid:    active.filter((p) => p.status === "paid").reduce((s, p) => s + (Number(p.amount) || 0), 0),
      pending: active.filter((p) => p.status === "pending" || p.status === "overdue")
        .reduce((s, p) => s + (Number(p.amount) || 0), 0),
    };
  }, [payments]);

  const archivedCount = useMemo(
    () => payments.filter((p) => p.status === "archived").length,
    [payments]
  );

  const { dupSet, dupCount } = useDuplicatePayments(payments);

  const visiblePayments = useMemo(() => {
    let out = showArchived ? payments : payments.filter((p) => p.status !== "archived");
    if (showOnlyDuplicates) {
      out = out.filter((p) => dupSet.has(p.payment_id));
    }
    return out;
  }, [payments, showArchived, showOnlyDuplicates, dupSet]);

  if (!investor) return null;

  return (
    <div data-testid="operaciones-tab-pagos">
      {villa?.villa_id && (
        <TermsCard
          investor={investor}
          villa={villa}
          paymentsReloadKey={payments.length}
        />
      )}

      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <div className="dv-card">
          <div className="text-xs text-stone-500">Pagado</div>
          <div className="text-2xl font-serif-dv">{eur(totals.paid)}</div>
        </div>
        <div className="dv-card">
          <div className="text-xs text-stone-500">Pendiente</div>
          <div className="text-2xl font-serif-dv">{eur(totals.pending)}</div>
        </div>
        <div className="dv-card flex items-center justify-center">
          <button
            className="dv-btn dv-btn-primary text-sm"
            onClick={() => { resetForm(); setShowForm((v) => !v); }}
            data-testid="pagos-create-toggle"
          >
            <Plus size={14} /> {showForm ? "Cancelar" : "Nuevo pago"}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 text-sm px-3 py-2 rounded-md border bg-red-50 border-red-200 text-red-800">
          {error}
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="dv-card mb-4 grid sm:grid-cols-3 gap-3" data-testid="pagos-form">
          {/* Title is the first / primary field — it is what the investor will
              see in the portal. Phase below is just an optional internal tag. */}
          <label className="sm:col-span-3 block">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">
              Nombre del pago *
            </div>
            <input
              className="dv-input"
              type="text"
              placeholder="Ej: Depósito inicial, Entrega de llaves 90%…"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
              maxLength={200}
              data-testid="pagos-form-title"
            />
          </label>
          <select
            className="dv-input"
            value={form.villa_id}
            onChange={(e) => setForm({ ...form, villa_id: e.target.value })}
            data-testid="pagos-form-villa"
          >
            <option value="">— Villa —</option>
            {villas.map((v) => (
              <option key={v.villa_id} value={v.villa_id}>{v.name || v.villa_id}</option>
            ))}
          </select>
          <select
            className="dv-input"
            value={form.phase}
            onChange={(e) => setForm({ ...form, phase: e.target.value })}
            data-testid="pagos-form-phase"
          >
            {PHASE_OPTIONS.map((p) => (
              <option key={p.value || "_none"} value={p.value}>{p.label}</option>
            ))}
          </select>
          <select
            className="dv-input"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            data-testid="pagos-form-status"
          >
            <option value="pending">Pendiente</option>
            <option value="paid">Pagado</option>
            <option value="overdue">Vencido</option>
          </select>
          <input
            className="dv-input"
            type="number"
            min="0"
            step="0.01"
            placeholder="Importe (EUR)"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            required
            data-testid="pagos-form-amount"
          />
          <input
            className="dv-input"
            type="text"
            placeholder="Moneda"
            value={form.currency}
            onChange={(e) => setForm({ ...form, currency: e.target.value })}
          />
          <input
            className="dv-input"
            type="date"
            value={form.due_date}
            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
            data-testid="pagos-form-due"
          />
          <div className="sm:col-span-3 flex gap-2">
            <button disabled={busy} className="dv-btn dv-btn-primary flex-1" data-testid="pagos-form-submit">
              {busy ? "Guardando…" : (editId ? "Guardar cambios" : "Crear pago")}
            </button>
            <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="dv-btn dv-btn-ghost">Cancelar</button>
          </div>
        </form>
      )}

      {/* Duplicate detection banner — pure client-side V1. Signature is
          investor+villa+phase+due_date+amount, ignoring archived rows. */}
      {dupCount > 0 && (
        <div
          className="mb-3 flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          data-testid="pagos-duplicate-banner"
        >
          <div>
            Hay <b>{dupCount}</b> posibles pagos duplicados. Revisa antes de enviar al inversor.
          </div>
          <label className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <input
              type="checkbox"
              checked={showOnlyDuplicates}
              onChange={(e) => setShowOnlyDuplicates(e.target.checked)}
              data-testid="pagos-toggle-duplicates"
            />
            Solo duplicados
          </label>
        </div>
      )}

      {/* Archived toggle / hint — only shown when there is at least one
          archived payment to keep the row uncluttered for new investors. */}
      {archivedCount > 0 && (
        <div className="flex justify-end mb-2 text-xs">
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            className="text-stone-500 hover:text-stone-800 inline-flex items-center gap-1"
            data-testid="pagos-toggle-archived"
          >
            {showArchived
              ? `Ocultar archivados (${archivedCount})`
              : `${archivedCount} pago${archivedCount === 1 ? "" : "s"} archivado${archivedCount === 1 ? "" : "s"} oculto${archivedCount === 1 ? "" : "s"} · Mostrar`}
          </button>
        </div>
      )}

      {loading ? (
        <div className="dv-card text-center py-6 text-stone-500 text-sm">Cargando…</div>
      ) : visiblePayments.length === 0 ? (
        <div className="dv-card text-center py-8 text-stone-500 text-sm">
          {payments.length === 0
            ? "Este inversor todavía no tiene pagos registrados."
            : "No hay pagos activos. Crea uno nuevo o muestra los archivados."}
        </div>
      ) : (
        <div className="dv-card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200 text-left">
              <tr className="text-xs uppercase tracking-wider text-stone-500">
                <th className="px-4 py-2">Villa</th>
                <th className="px-4 py-2">Pago</th>
                <th className="px-4 py-2 text-right">Importe</th>
                <th className="px-4 py-2">Vencimiento</th>
                <th className="px-4 py-2">Estado</th>
                <th className="px-4 py-2 text-right">Acción</th>
              </tr>
            </thead>
            <tbody>
              {visiblePayments.map((p) => {
                const villaName = villas.find((v) => v.villa_id === p.villa_id)?.name || p.villa_id;
                const isArchived = p.status === "archived";
                const isDup = dupSet.has(p.payment_id);
                return (
                  <tr
                    key={p.payment_id}
                    className={`border-b border-stone-100 ${isArchived ? "opacity-60" : ""}`}
                    data-testid={`pagos-row-${p.payment_id}`}
                  >
                    <td className="px-4 py-2">{villaName}</td>
                    <td className="px-4 py-2 text-stone-700">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{paymentLabel(p)}</span>
                        {isDup && (
                          <span
                            className="inline-flex text-[10px] px-1.5 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200"
                            title="Otro pago activo tiene el mismo inversor, villa, fase, vencimiento e importe."
                            data-testid={`pagos-duplicate-badge-${p.payment_id}`}
                          >
                            Posible duplicado
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right font-medium">{eur(p.amount)}</td>
                    <td className="px-4 py-2 text-stone-600">{p.due_date || "—"}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex text-[11px] px-2 py-0.5 rounded-full border ${STATUS_TONE[p.status] || STATUS_TONE.pending}`}>
                        {STATUS_LABEL[p.status] || p.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => startEdit(p)}
                        className="text-xs px-2 py-1 rounded border border-stone-300 text-stone-700 hover:bg-stone-100 mr-1"
                        data-testid={`pagos-edit-${p.payment_id}`}
                      >Editar</button>
                      {p.status !== "paid" && !isArchived && (
                        <button
                          onClick={() => markPaid(p)}
                          className="text-xs px-2 py-1 rounded border border-emerald-200 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1 mr-1"
                          data-testid={`pagos-paid-${p.payment_id}`}
                        >
                          <CheckCircle size={12} /> Marcar pagado
                        </button>
                      )}
                      {!isArchived && (
                        <button
                          onClick={() => archive(p)}
                          title="Se ocultará al inversor pero se conservará el histórico."
                          className="text-xs px-2 py-1 rounded border border-stone-300 text-stone-600 hover:bg-stone-100 inline-flex items-center gap-1"
                          data-testid={`pagos-archive-${p.payment_id}`}
                        >
                          <Archive size={12} /> Archivar pago
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * TermsCard — admin-only financial terms editor for an (investor, villa) pair.
 *
 * Loads from GET /investor-terms/{investor}/{villa}, lets the admin edit
 * agreed_price_eur, plan_mode and an internal note, and persists via PUT.
 * The card also surfaces the live diff between the agreed price and the sum
 * of the active payments so the admin can see at a glance whether the plan
 * adds up. Reload key is bumped from the parent when payments change so the
 * "Suma actual" recalculates after create/edit/archive.
 */
function TermsCard({ investor, villa, paymentsReloadKey }) {
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({
    agreed_price_eur: "",
    plan_mode: "standard",
    note: "",
  });

  const load = async () => {
    if (!investor?.user_id || !villa?.villa_id) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/investor-terms/${investor.user_id}/${villa.villa_id}`
      );
      setTerms(data);
      setForm({
        agreed_price_eur: data.agreed_price_eur_overridden
          ? String(data.agreed_price_eur ?? "")
          : "",
        plan_mode: data.plan_mode || "standard",
        note: data.note || "",
      });
      setError("");
    } catch (e) {
      setError(formatError(e));
      setTerms(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investor?.user_id, villa?.villa_id, paymentsReloadKey]);

  const save = async () => {
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const trimmed = String(form.agreed_price_eur || "").trim();
      const payload = {
        agreed_price_eur: trimmed === "" ? null : Number(trimmed),
        plan_mode: form.plan_mode,
        note: form.note || "",
      };
      if (payload.agreed_price_eur != null && (!Number.isFinite(payload.agreed_price_eur) || payload.agreed_price_eur <= 0)) {
        setError("Precio acordado debe ser un número mayor que 0.");
        setBusy(false);
        return;
      }
      const { data } = await api.put(
        `/investor-terms/${investor.user_id}/${villa.villa_id}`,
        payload,
      );
      setTerms(data);
      setSuccess("Términos guardados.");
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="dv-card mb-4 text-sm text-stone-500" data-testid="pagos-terms-loading">
        Cargando términos financieros…
      </div>
    );
  }
  if (!terms) {
    return (
      <div className="dv-card mb-4 text-sm text-red-700" data-testid="pagos-terms-error">
        {error || "No se pudieron cargar los términos financieros."}
      </div>
    );
  }

  const matches = !!terms.payments_match_price;
  const diff = Number(terms.difference) || 0;

  return (
    <div className="dv-card mb-4" data-testid="pagos-terms-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500">
            Términos financieros
          </div>
          <div className="text-sm text-stone-700 mt-1">
            {investor.name || investor.email} · {villa.name || villa.villa_id}
          </div>
        </div>
        {terms.agreed_price_eur_overridden ? (
          <span className="text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200">
            Precio personalizado
          </span>
        ) : (
          <span className="text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full border bg-stone-50 text-stone-600 border-stone-200">
            Precio base villa
          </span>
        )}
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-3">
        <label className="block">
          <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">
            Precio acordado (€)
          </div>
          <input
            type="number"
            min="0"
            step="100"
            className="dv-input"
            placeholder={`Por defecto: ${eur(terms.villa_price_eur)}`}
            value={form.agreed_price_eur}
            onChange={(e) => setForm((f) => ({ ...f, agreed_price_eur: e.target.value }))}
            data-testid="pagos-terms-price"
          />
          <div className="text-[10px] text-stone-400 mt-1">
            Deja vacío para usar el precio base de la villa.
          </div>
        </label>

        <label className="block">
          <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">
            Plan de pagos
          </div>
          <select
            className="dv-input"
            value={form.plan_mode}
            onChange={(e) => setForm((f) => ({ ...f, plan_mode: e.target.value }))}
            data-testid="pagos-terms-plan-mode"
          >
            <option value="standard">Usar plan estándar (10/40/30/20)</option>
            <option value="custom">Personalizar plan</option>
          </select>
          <div className="text-[10px] text-stone-400 mt-1">
            El modo es informativo: las líneas de pago se editan abajo.
          </div>
        </label>

        <label className="block">
          <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">
            Nota interna (admin)
          </div>
          <textarea
            rows={3}
            className="dv-input"
            placeholder="Contexto del acuerdo, condiciones especiales…"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            data-testid="pagos-terms-note"
          />
          <div className="text-[10px] text-stone-400 mt-1">
            Visible solo para admin. El inversor nunca la ve.
          </div>
        </label>
      </div>

      <div className="grid sm:grid-cols-4 gap-3 text-sm" data-testid="pagos-terms-summary">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Precio acordado</div>
          <div className="font-serif-dv text-lg">{eur(terms.agreed_price_eur)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Suma pagos actuales</div>
          <div className="font-serif-dv text-lg">{eur(terms.payments_total_amount)}</div>
          <div className="text-[10px] text-stone-400">{terms.payments_count} pago(s)</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Diferencia</div>
          <div className="font-serif-dv text-lg">
            {diff === 0 ? "—" : `${diff > 0 ? "+" : ""}${eur(diff)}`}
          </div>
        </div>
        <div className="flex items-end">
          {matches ? (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="pagos-terms-match-ok">
              <CheckCircle size={14} weight="fill" /> Cuadra
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border bg-amber-50 text-amber-800 border-amber-200" data-testid="pagos-terms-match-warn">
              ⚠ No cuadra
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-3 text-sm px-3 py-2 rounded-md border bg-red-50 border-red-200 text-red-800">
          {error}
        </div>
      )}
      {success && (
        <div className="mt-3 text-sm px-3 py-2 rounded-md border bg-emerald-50 border-emerald-200 text-emerald-800">
          {success}
        </div>
      )}

      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="dv-btn dv-btn-primary text-sm"
          data-testid="pagos-terms-save"
        >
          {busy ? "Guardando…" : "Guardar términos"}
        </button>
      </div>
    </div>
  );
}
